import jsPDF from "jspdf";
import type {
  CutPiece,
  PipeRun,
  RunFitting,
  SupportPlacement,
  TerminationPlacement,
  Vec3,
} from "@/hooks/use-pipe-sketch";
import { formatElevation, formatFtIn, getMaterial, type MaterialId } from "@/lib/pipe-spec";

const MARGIN = 15;
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;
const COS30 = Math.cos(Math.PI / 6);

interface Totals {
  totalFeet: number;
  totalWeight: number;
  supportWeight: number;
  bySize: [string, { feet: number; weight: number; pieces: number }][];
  fittingCounts: [string, number][];
  componentCounts: [string, number][];
  supportCounts: [string, number][];
  terminationCounts: [string, number][];
  welds: number;
  pieces: number;
}

export interface SpoolSheetInput {
  name: string;
  material: MaterialId;
  runs: PipeRun[];
  fittings: RunFitting[];
  cutList: CutPiece[];
  supports: SupportPlacement[];
  terminations: TerminationPlacement[];
  totals: Totals;
  baseElevation: number;
}

/** Isometric projection, unscaled. */
const project = (p: Vec3) => ({
  x: (p.x - p.y) * COS30,
  y: (p.x + p.y) * 0.5 - p.z,
});

/** A4 spool sheet: ISO drawing on top, cut list and schedules below. */
export function exportSpoolSheet(input: SpoolSheetInput) {
  const { name, material, runs, fittings, cutList, supports, terminations, totals } = input;
  const mat = getMaterial(material);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  const setGray = (v: number) => doc.setTextColor(v, v, v);

  /* ---------- title block ---------- */
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  setGray(35);
  doc.text(name || "Pipe spool", MARGIN, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setGray(110);
  doc.text(
    `${mat.label} · ${mat.spec} · ${mat.joint} · Generated ${new Date().toLocaleString()}`,
    MARGIN,
    y,
  );
  y += 4;
  doc.setDrawColor(200);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 6;

  /* ---------- isometric drawing ---------- */
  const boxH = 105;
  const boxTop = y;
  doc.setDrawColor(220);
  doc.rect(MARGIN, boxTop, CONTENT_W, boxH);

  const pts: Vec3[] = runs.flatMap((r) => r.pts);
  if (pts.length) {
    const proj = pts.map(project);
    const minX = Math.min(...proj.map((p) => p.x));
    const maxX = Math.max(...proj.map((p) => p.x));
    const minY = Math.min(...proj.map((p) => p.y));
    const maxY = Math.max(...proj.map((p) => p.y));
    const pad = 14;
    const sx = (CONTENT_W - pad * 2) / Math.max(1, maxX - minX);
    const sy = (boxH - pad * 2) / Math.max(1, maxY - minY);
    const s = Math.min(sx, sy, 3);
    const ox = MARGIN + CONTENT_W / 2 - ((minX + maxX) / 2) * s;
    const oy = boxTop + boxH / 2 - ((minY + maxY) / 2) * s;
    const to = (p: Vec3) => {
      const q = project(p);
      return { x: ox + q.x * s, y: oy + q.y * s };
    };

    // pipe centrelines
    doc.setDrawColor(30);
    doc.setLineWidth(0.7);
    for (const run of runs) {
      for (let i = 0; i < run.pts.length - 1; i++) {
        const a = to(run.pts[i]);
        const b = to(run.pts[i + 1]);
        doc.line(a.x, a.y, b.x, b.y);
      }
    }

    // joints
    doc.setFillColor(30, 30, 30);
    for (const f of fittings) {
      const p = to(f.at);
      doc.circle(p.x, p.y, 0.7, "F");
    }

    // piece marks + lengths
    doc.setFontSize(6);
    setGray(90);
    for (const p of cutList) {
      const a = to(p.from);
      const b = to(p.to);
      doc.text(`${p.tag} ${formatFtIn(p.cutLength)}`, (a.x + b.x) / 2 + 1.5, (a.y + b.y) / 2 - 1.2);
    }

    // supports
    doc.setDrawColor(30);
    doc.setLineWidth(0.4);
    for (const sup of supports) {
      const p = to(sup.at);
      doc.line(p.x - 1.6, p.y - 3.2, p.x + 1.6, p.y - 3.2);
      doc.line(p.x, p.y - 3.2, p.x, p.y);
      doc.text(sup.mark, p.x + 2, p.y - 3.4);
    }

    // terminations
    for (const t of terminations) {
      const p = to(t.at);
      doc.rect(p.x - 1.3, p.y - 1.3, 2.6, 2.6);
      doc.text(`${t.tag ?? t.mark} ${t.short}`, p.x + 2.2, p.y + 3);
    }

    // north arrow
    const nx = PAGE_W - MARGIN - 10;
    const ny = boxTop + 12;
    doc.setLineWidth(0.5);
    doc.line(nx, ny + 5, nx + 4, ny - 4);
    doc.line(nx + 4, ny - 4, nx + 8, ny + 5);
    doc.setFontSize(7);
    setGray(60);
    doc.text("N", nx + 3.2, ny + 9);
  } else {
    doc.setFontSize(9);
    setGray(150);
    doc.text("No geometry drawn", PAGE_W / 2, boxTop + boxH / 2, { align: "center" });
  }
  y = boxTop + boxH + 8;

  /* ---------- table helpers ---------- */
  const checkPage = (needed: number) => {
    if (y + needed > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const heading = (text: string) => {
    checkPage(12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setGray(35);
    doc.text(text, MARGIN, y);
    y += 2;
    doc.setDrawColor(210);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
  };

  const row = (cells: string[], widths: number[], bold = false) => {
    checkPage(6);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    setGray(bold ? 40 : 80);
    let x = MARGIN;
    cells.forEach((c, i) => {
      doc.text(c, x, y);
      x += widths[i];
    });
    y += 4.6;
  };

  /* ---------- cut list ---------- */
  heading("Cut list");
  const cw = [18, 22, 34, 34, CONTENT_W - 108];
  row(["MARK", "SIZE", "CUT LENGTH", "C/C LENGTH", "JOINT ENDS"], cw, true);
  for (const p of cutList) {
    row(
      [
        p.tag,
        p.size,
        formatFtIn(p.cutLength),
        formatFtIn(p.centerLength),
        String(p.weldEnds),
      ],
      cw,
    );
  }
  y += 3;

  /* ---------- fittings ---------- */
  if (totals.fittingCounts.length) {
    heading("Fittings");
    for (const [k, n] of totals.fittingCounts) row([`${n} ×`, k], [14, CONTENT_W - 14]);
    y += 3;
  }

  if (totals.componentCounts.length) {
    heading("Valves & specialties");
    for (const [k, n] of totals.componentCounts) row([`${n} ×`, k], [14, CONTENT_W - 14]);
    y += 3;
  }

  /* ---------- support schedule ---------- */
  if (supports.length) {
    heading("Support schedule");
    const sw = [18, 22, 34, 26, CONTENT_W - 100];
    row(["MARK", "SIZE", "TYPE", "ROD", "DETAIL"], sw, true);
    for (const s of supports) {
      row([s.mark, s.size, s.label, s.rodSize || "—", s.detail], sw);
    }
    y += 3;
  }

  /* ---------- terminations ---------- */
  if (terminations.length) {
    heading("Terminations & equipment");
    const tw = [18, 22, 40, CONTENT_W - 80];
    row(["MARK", "SIZE", "TYPE", "DETAIL"], tw, true);
    for (const t of terminations) {
      row([t.tag ?? t.mark, t.size, t.label, t.detail], tw);
    }
    y += 3;
  }

  /* ---------- totals ---------- */
  heading("Totals");
  const tw2 = [70, CONTENT_W - 70];
  for (const [sz, v] of totals.bySize) {
    row([`${sz} pipe`, `${v.feet.toFixed(2)} ft · ${v.weight.toFixed(0)} lb`], tw2);
  }
  row(["Total pipe", `${totals.totalFeet.toFixed(2)} ft`], tw2, true);
  row(["Est. pipe + component weight", `${totals.totalWeight.toFixed(1)} lb`], tw2, true);
  if (totals.supportWeight)
    row(["Support hardware", `${totals.supportWeight.toFixed(1)} lb`], tw2);
  row([mat.jointType === "bw" ? "Field welds" : "Joints", String(totals.welds)], tw2);
  row(["Datum", `EL ${formatElevation(input.baseElevation)} at Z = 0`], tw2);

  doc.save(`${(name || "pipe-spool").replace(/[^\w-]+/g, "-").toLowerCase()}.pdf`);
}
